import EditorActions from 'components/Common/EditorActions'
import ProfileEditError from '../ProfileEditError'
import ProfileEditResult from '../ProfileEditResult'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { useFormik } from 'formik'
import { Container, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import Breadcrumbs from 'components/Common/Breadcrumb'
import { anyPending, heldSaveMessage, showsCommand, typedTranslations, useTranslationRefresh } from 'components/Common/localizedText'
import useConfirmedOperation from 'components/Common/useConfirmedOperation'
import ListenerComponent from 'components/Common/Socket/ListenerComponent'
import { store as socketStore } from 'components/Common/Socket/store'
import { get, post } from 'helpers/api_helper'
import EditorNavigation from '../EditorNavigation'
import { newPlayerOperationId } from '../PlayerAccounts/playerInvitationState'
import { focusProfileField } from '../profileValidationFields'
import useProfileEditOperation from '../useProfileEditOperation'
import ClubEditFields from './ClubEditFields'
import ClubEditMedia from './ClubEditMedia'
import ClubTextBlocks from './ClubTextBlocks'
import { CLUB_TEXT_BLOCKS, clubTextOriginal, originalBody } from './clubTexts'
import { initialClubEditValues, buildClubEditPayload, validateClubEdit } from './clubEditForm'
import './clubCreation.scss'

const FORBIDDEN_TEXT = 'Your account no longer has permission to edit this club.'
// What the author is told of a command on a text the API refused (useConfirmedOperation's `refusals`): a refused text that names its
// field (`INVALID_CLUB_TEXT:content.body`) gets the field's sentence, and the club's failure says the command was not saved.
const TEXT_COMMAND_REFUSALS = {
  INVALID_TRANSLATION_STATE: 'The variant changed meanwhile. Reload the page to see its state, and try again.',
  INVALID_TRANSLATION_LANGUAGE: 'This language cannot be chosen for this text.',
  INVALID_TRANSLATION_CONTENT: 'Fill in every field the original has.',
  'INVALID_CLUB_TEXT:*': 'Write the variant within the length of the original\'s field, and try again.',
  PROFILE_EDIT_IN_PROGRESS: 'A save of this club is in progress. Try again once it is saved.',
  CLUB_NOT_AVAILABLE: 'This club is no longer available.',
  CLUB_UPDATE_FORBIDDEN: FORBIDDEN_TEXT,
  ERROR_PERMISSIONS: FORBIDDEN_TEXT,
  FORBIDDEN: FORBIDDEN_TEXT,
  UPDATE_CLUB_TEXTS_FAILED: 'The command could not be saved. Try again.',
}
const TEXTS_UPDATED = 'SUCCESSFULLY_UPDATED_CLUB_TEXTS'

function ClubEditor({ account: opened, options, userDetails, id, location, reload }) {
  const { state: { socket: { socketClientId } = {} } = {} } = useContext(socketStore) || {}
  // The club as the editor last read it: a confirmed language changes its texts and its revision.
  const [account, setAccount] = useState(opened)
  const command = useProfileEditOperation({ actorId: userDetails.id || userDetails._id, id, kind: 'club' })
  const media = useRef(null), [mediaState, setMediaState] = useState({ dirty: false, uploading: false })
  const save = async payload => {
    try { const images = await media.current.upload(); media.current.retain(); await command.save({ ...payload, profile: { ...payload.profile, ...images } }) } catch { /* Upload errors remain beside their image. */ }
  }
  useEffect(() => { if (command.status === 'error') media.current?.release() }, [command.status])
  const alive = useRef(true)
  useEffect(() => () => { alive.current = false }, [])
  const [submitted, setSubmitted] = useState(false)
  const [addressBusy, setAddressBusy] = useState(false), [organizerBusy, setOrganizerBusy] = useState({})
  const [confirmation, setConfirmation] = useState(null)
  const permitted = ['admin', 'superAdmin'].includes(userDetails.role) && userDetails.status === 'activated'
    && account.profile.status !== 'deleted' && account.profile.ownerStatus !== 'deleted'
  const form = useFormik({ initialValues: initialClubEditValues(account.profile, account.localizedTexts), validate: values => validateClubEdit(values, options, account.profile),
    onSubmit: values => {
      const profile = buildClubEditPayload(values, account.profile), emailChanged = profile.ownerInfo.email !== account.profile.ownerInfo.email.toLowerCase()
      const organizerDisabled = account.profile.isOrganizer && !profile.isOrganizer
      const payload = { revision: account.revision, profile, confirmations: {
        ...(emailChanged ? { email: profile.ownerInfo.email } : {}), ...(organizerDisabled ? { organizerDisabled: true } : {}),
      } }
      if (emailChanged || organizerDisabled) setConfirmation({ payload, emailChanged, organizerDisabled })
      else return save(payload)
    } })
  useEffect(() => setConfirmation(null), [form.values])
  // The author's commands on the club's texts (ADR 0013), on the club's own route: the API answers on the socket, and a read of the club
  // that shows a command confirms one whose answer is lost.
  const texts = useConfirmedOperation()
  // The manual variants typed and not saved yet, by block and then by language, and whether a save waited for them.
  const [manualDrafts, setManualDrafts] = useState({})
  const [heldSave, setHeldSave] = useState(false)
  const unsavedTranslations = Object.fromEntries(Object.keys(CLUB_TEXT_BLOCKS).map(name => {
    const block = account.localizedTexts?.[name], { language, content } = clubTextOriginal(form.values, name)
    const originalChanged = !block || language !== block.originalLanguage || content.body !== originalBody(block)
    return [name, typedTranslations(manualDrafts[name] || {}, block, originalChanged)]
  }))
  const textDirty = Object.values(unsavedTranslations).some(languages => languages.length > 0)
  // Once no typed variant waits, a later one does not start with the held save's message.
  useEffect(() => { if (!textDirty) setHeldSave(false) }, [textDirty])
  const readClub = () => get('/admin/club-profiles/' + id)
  // The club's texts the editor shows come from a command's answer, a read of the whole club, or a refresh; each but the refresh is
  // numbered, and a refresh or a read that started before the last one is dropped.
  const textsVersion = useRef(0)
  const showTexts = localizedTexts => {
    textsVersion.current += 1
    setAccount(current => ({ ...current, localizedTexts: { ...current.localizedTexts, ...localizedTexts } }))
  }
  // The editor, without other changes, follows the club as a read gives it: its profile, its texts and its revision.
  const follow = data => { setAccount(data); form.resetForm({ values: initialClubEditValues(data.profile, data.localizedTexts) }) }
  // A confirmed language can change the original's language and the profile's revision, so the editor, which had no other changes,
  // follows the club read again; it stays locked meanwhile, so nothing typed is lost to the read.
  const [reopening, setReopening] = useState(false)
  const reopen = async () => {
    const version = ++textsVersion.current
    setReopening(true)
    try {
      const { data } = await readClub()
      if (alive.current && version === textsVersion.current && data?.profile && data.revision) follow(data)
    } catch { /* A refresh while the variants are made follows the new revision; a save meets it otherwise. */ }
    if (alive.current) setReopening(false)
  }
  const runText = (name, { action, language, content }) => {
    const operationId = newPlayerOperationId()
    texts.run({
      execute: () => post('/admin/clubs/' + id + '/texts', { operationId, socketClientId, block: name, action, language, ...(content ? { content } : {}) }),
      read: readClub,
      isComplete: result => showsCommand({ action, language }, result?.data?.localizedTexts?.[name]),
      matchesSocket: payload => payload.operationId === operationId && (!payload.success || payload.message === TEXTS_UPDATED),
      refusals: TEXT_COMMAND_REFUSALS,
      // A variant written by hand is current from then on, so what was typed for it no longer counts (typedTranslations).
      onSuccess: result => {
        showTexts(result.data?.localizedTexts)
        if (action === 'confirmLanguage') reopen()
      },
    })
  }
  const textCommands = name => permitted ? {
    confirm: language => runText(name, { action: 'confirmLanguage', language }),
    translate: (language, content) => runText(name, { action: 'translateManually', language, content }),
    confirmUnavailable: form.dirty || mediaState.dirty ? 'Save or cancel your other changes first.' : null,
  } : undefined
  // The variants read again while they are made. A read of the revision the editor holds gives only the texts, and its values stay as they
  // are; a read of another revision (a confirmed language the editor could not read, or a save elsewhere) is followed by an editor without
  // other changes.
  const refreshTexts = async () => {
    const version = textsVersion.current
    const { data } = await readClub()
    if (!alive.current || version !== textsVersion.current || !data?.localizedTexts) return
    if (data.revision === account.revision) setAccount(current => ({ ...current, localizedTexts: data.localizedTexts }))
    else if (!form.dirty && !mediaState.dirty && data.profile) follow(data)
  }
  useTranslationRefresh(anyPending(account.localizedTexts) && command.status !== 'saved', refreshTexts, account.revision)
  const locked = command.locked || mediaState.uploading || !permitted || texts.busy || reopening
  const choicesBusy = addressBusy || Object.values(organizerBusy).some(Boolean)
  const submit = async event => {
    event.preventDefault(); if (locked || choicesBusy) return
    setSubmitted(true)
    const errors = await form.validateForm(), first = Object.keys(errors)[0]
    if (first) {
      const element = document.getElementById('club-' + first) || document.getElementById('player-' + first)
      const details = element?.closest('details'); if (details) details.open = true
      element?.focus(); return
    }
    // A manual variant typed and not saved would be lost: the save waits for it.
    const held = Object.keys(unsavedTranslations).find(name => unsavedTranslations[name].length)
    setHeldSave(Boolean(held))
    if (held) { focusProfileField('club-' + held + '-translation'); return }
    await form.submitForm()
  }
  const back = { pathname: '/accounts/clubs/' + id, state: location.state }
  if (command.status === 'saved') return <ProfileEditResult command={command} back={back} label="Open club profile" reload={reload} club />
  const textBlocks = <>
    <ClubTextBlocks form={form} locked={locked} blocks={account.localizedTexts} commands={textCommands} drafts={manualDrafts} onDrafts={setManualDrafts}
      errors={Object.fromEntries(Object.entries(unsavedTranslations).map(([name, languages]) => [name,
        heldSave && languages.length ? heldSaveMessage(languages, 'club') : undefined]))} />
    {texts.message && <p className="text-danger mt-3 mb-0" role="alert">{texts.message}</p>}
  </>
  return <>
    <ListenerComponent topic="clubs-response" listenFunction={texts.receive} />
    <EditorNavigation dirty={form.dirty || mediaState.dirty || textDirty} pending={command.locked || texts.busy} />
    <div className="d-flex flex-wrap align-items-center justify-content-between mb-3"><div><h4>{account.profile.clubName}</h4>
      <span className="text-muted">#{account.profile.clubId} · Club: {account.profile.status} · Owner: {account.profile.ownerStatus}</span></div>
      <Link className="btn btn-light" to={back}>Back to profile</Link></div>
    {!permitted && <p className="alert alert-info" role="status">This club and owner can only be viewed with the current account state.</p>}
    <Modal isOpen={Boolean(confirmation)} toggle={() => setConfirmation(null)}><ModalHeader toggle={() => setConfirmation(null)}>
      {confirmation?.emailChanged ? 'Change owner sign-in email?' : 'Disable organizer access?'}</ModalHeader>
      <ModalBody>{confirmation?.emailChanged && <><p>{account.profile.ownerInfo.email} → {confirmation.payload.profile.ownerInfo.email}</p>
        <p>The password and status are preserved. Unused invitation and reset links become invalid. Send a new invitation explicitly from the profile if needed.</p></>}
        {confirmation?.organizerDisabled && <p>SportyaOS access is revoked. Existing organizer details, assigned clubs and events are preserved.</p>}</ModalBody>
      <ModalFooter><button type="button" className="btn btn-light" onClick={() => setConfirmation(null)}>Keep editing</button>
        <button type="button" className="btn btn-primary" onClick={() => { const { payload } = confirmation; setConfirmation(null); save(payload) }}>Confirm and save</button></ModalFooter></Modal>
    <form aria-label="Edit club" noValidate onSubmit={submit}>
      <ProfileEditError command={command} reload={reload} />
    {command.locked && <div className="alert alert-info" role="status">{command.status === 'unknown' ? 'Saving has not been confirmed.' : 'Waiting for club and owner confirmation…'}
        {command.status === 'unknown' && <><button type="button" className="btn btn-light" onClick={command.check}>Check save status</button><button type="button" className="btn btn-light" onClick={command.resume}>Resume save</button></>}</div>}
      {submitted && Object.keys(form.errors).length > 0 && <div className="alert alert-danger" role="alert">Complete the highlighted required details before saving.</div>}
      <ClubEditFields form={form} options={options} submitted={submitted} locked={locked} setAddressBusy={setAddressBusy} setOrganizerBusy={setOrganizerBusy}
        textBlocks={textBlocks} />
      <ClubEditMedia ref={media} id={id} profile={account.profile} categories={options.categories} locked={locked} onState={setMediaState} />
      {/* The footer waits only for a save, an upload, a command on a text or the read after it: a club that can only be viewed has nothing
        being saved. */}
      <EditorActions dirty={form.dirty || mediaState.dirty || textDirty} pending={command.locked || mediaState.uploading || texts.busy || reopening}>
        <Link className="btn btn-light" to={back}>Cancel</Link>{permitted && <button type="submit" className="btn btn-primary" disabled={locked || choicesBusy}>Save changes</button>}
        {texts.phase === 'uncertain' && <button type="button" className="btn btn-light" onClick={texts.verify}>Check the translation command</button>}
      </EditorActions>
    </form>
  </>
}
export function EditClub({ userDetails, ...props }) {
  const id = props.match.params.id, actorId = userDetails.id || userDetails._id
  const [data, setData] = useState(null), [error, setError] = useState(''), [retry, setRetry] = useState(0)
  useEffect(() => {
    let active = true; setData(null); setError('')
    Promise.all([get('/admin/club-profiles/' + id), get('/settings')]).then(([account, settings]) => {
      if (!account.data?.profile || !account.data?.revision || !['activeSports', 'eligibleCountries', 'courtSurfaces'].every(key => Array.isArray(settings.data?.[key]))) throw new Error('Unavailable club')
      if (active) setData({ account: account.data, options: settings.data })
    }).catch(() => { if (active) setError('Could not load the club, its owner and available options.') })
    return () => { active = false }
  }, [id, actorId, retry])
  return <div className="page-content"><Container fluid><Breadcrumbs title="Clubs" path={{ pathname: "/accounts/clubs", search: props.location?.state?.clubListSearch || "", state: props.location?.state }} breadcrumbItem="Edit club" />
    <div className="club-creation">{error ? <div role="alert">{error} <button type="button" className="btn btn-primary" onClick={() => setRetry(value => value + 1)}>Retry club</button></div>
      : !data ? <p role="status">Loading club and owner…</p> : <ClubEditor key={actorId + ':' + id + ':' + retry} {...props} {...data} id={id} userDetails={userDetails} reload={() => setRetry(value => value + 1)} />}</div>
  </Container></div>
}
export default function ConnectedEditClub(props) {
  const userDetails = useSelector(state => state.session.userDetails) || {}
  return <EditClub {...props} userDetails={userDetails} />
}
